import { useMemo, useCallback } from 'react';
import { useMutation, useQuery, InfiniteData, useInfiniteQuery } from 'react-query';
import produce from 'immer';
import message from 'antd/es/message';

import { queryClient, request, useCurrentUserId, toastError } from '@/utils';

import { FeedArticleDetail, FeedDrawJoiner } from '../types';
import { setQueryAllFeedDetailByArticleId } from './setQuery';
import { transAuthor } from '../utils';

// 查看是否可以参与抽奖
export const useFeedCanJoin = (id: number) => {
  const currentAddress = useCurrentUserId();
  const url = '/feed/draw/can_join';

  return useQuery(
    [url, currentAddress, id],
    () => {
      return request.get<{
        can_join: boolean;
        details: {
          follow_creator: boolean;
          repost_article: boolean;
        };
      }>(url, {
        id,
      });
    },
    {
      enabled: !!id,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
      onError(error: any) {
        toastError(error);
      },
    },
  );
};

export const invalidStreamDrawCanJoin = (id: number, currentAddress?: string) => {
  queryClient.invalidateQueries(['/feed/draw/can_join', currentAddress, id]);
};

export const useFeedJoinDraw = (articleId: number, id: number) => {
  const currentAddress = useCurrentUserId();

  return useMutation(
    () =>
      request.post<{ is_success: boolean; inner_id: number }>('/feed/draw/join', {
        id,
      }),
    {
      onSuccess: () => {
        message.success('Joined');
        const recipe = (pre?: FeedArticleDetail) => {
          console.log('pre?.article?.draw', pre?.article?.draw);
          if (pre?.article?.draw) {
            pre.article.draw.is_joined = true;
            pre.article.draw.join_count += 1;
          }
        };

        setQueryAllFeedDetailByArticleId({
          currentAddress: currentAddress!,
          articleId: articleId,
          recipe,
          includeQuote: true,
        });
      },
      onError(err: any) {
        toastError(err || 'failed to join');
      },
    },
  );
};

const SEARCH_PAGE_LIMIT = 100;

export const useFeedDrawJoiners = (drawId: number, is_winner?: boolean) => {
  const currentId = useCurrentUserId();
  const url = '/feed/draw/join_users';

  const select = useCallback(
    (data: InfiniteData<{ users: FeedDrawJoiner[]; total_count: number }>) => {
      return produce(data, draft => {
        draft.pages.forEach(page =>
          page.users?.forEach(x => {
            x._author = transAuthor({ user: x });
          }),
        );
      });
    },
    [],
  );

  const queryKey = useMemo(
    () => [url, drawId, currentId, is_winner],
    [url, drawId, currentId, is_winner],
  );

  const {
    data,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
    refetch,
    isFetching,
  } = useInfiniteQuery(
    queryKey,
    async ({ pageParam }) => {
      const params: Record<string, number | string | boolean> = {
        limit: SEARCH_PAGE_LIMIT,
        start: 0,
        id: drawId,
      };

      if (is_winner) {
        params.is_winner = is_winner;
      }

      if (pageParam) {
        Object.assign(params, pageParam);
      }

      return request.get<{ users: FeedDrawJoiner[]; total_count: number }>(url, params);
    },
    {
      keepPreviousData: false,
      getNextPageParam: (lastPage, pages) => {
        const length = lastPage.users?.length;
        if (length >= SEARCH_PAGE_LIMIT) {
          return {
            start: pages.length * SEARCH_PAGE_LIMIT,
          };
        }
        // Return false means no next page
        return false;
      },
      select,
      enabled: !!drawId,
      onError(err: any) {
        toastError(err);
      },
    },
  );

  const result = useMemo(
    () => ({
      list: data?.pages?.flatMap(x => x.users),
      totalCount: data?.pages[0]?.total_count,
    }),
    [data],
  );

  return {
    result,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    isFetching,
    isFetchingPreviousPage,
    fetchPreviousPage,
  };
};

export const useGetAllFeedDrawWinner = (drawId: number) => {
  const url = '/feed/draw/join_users';
  const params = {
    // 所有数据暂时不会超过1000
    limit: 1000,
    start: 0,
    id: drawId,
    is_winner: true,
  };
  return useMutation(() =>
    request.get<{ users: FeedDrawJoiner[]; total_count: number }>(url, params),
  );
};
